
import React from 'react';
import { Joker } from '../types';
import { RARITY_COLORS } from '../constants';
import JokerArt from './JokerArt';

interface JokerInspectorProps {
  joker: Joker;
  onClose: () => void;
  onActivate: (id: string) => void;
  isAbilityFlashing: boolean;
}

const JokerInspector: React.FC<JokerInspectorProps> = ({ joker, onClose, onActivate, isAbilityFlashing }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-2xl animate-in fade-in duration-300 p-4" onClick={onClose}>
      <div 
        className={`joker-card-float relative w-full max-w-[340px] bg-[#0c0c0c] rounded-[2.5rem] border-[8px] ${RARITY_COLORS[joker.rarity as keyof typeof RARITY_COLORS]} flex flex-col items-center shadow-[0_0_80px_rgba(0,0,0,1)] overflow-hidden ${isAbilityFlashing ? 'joker-use-pulse' : ''}`} 
        onClick={e => e.stopPropagation()}
      >
         {/* Internal Card Frame */}
         <div className="absolute inset-2 border border-white/5 rounded-[2rem] pointer-events-none z-0" />
         
         {/* Texture & Holographic Overlays */}
         <div className="absolute inset-0 card-texture opacity-30 pointer-events-none z-10" />
         {(joker.rarity === 'Rare' || joker.rarity === 'Legendary') && (
            <div className="absolute inset-0 holo-foil pointer-events-none opacity-20 z-20" />
         )}

         {/* Card Header */}
         <div className="w-full px-6 pt-8 pb-4 text-center z-30">
            <div className={`text-[10px] font-black uppercase tracking-[0.6em] mb-2 opacity-40 ${RARITY_COLORS[joker.rarity as keyof typeof RARITY_COLORS]}`}>
              {joker.rarity}
            </div>
            <h2 className="text-3xl md:text-4xl font-black italic uppercase tracking-tighter text-white drop-shadow-[0_2px_10px_rgba(255,255,255,0.2)]">
              {joker.name}
            </h2>
         </div>

         {/* Central Illustration Area */}
         <div className="w-full px-8 z-30">
            <JokerArt id={joker.id} rarity={joker.rarity} size="lg" />
         </div>

         {/* Mechanics & Flavor Section */}
         <div className="w-full px-8 pb-10 z-30 flex-1 flex flex-col">
            <div className="bg-black/80 p-5 rounded-2xl border border-white/10 shadow-inner flex-1 flex flex-col justify-center gap-4">
              <p className="text-lg md:text-xl text-white/90 italic text-center font-vt323 leading-snug tracking-wide">
                "{joker.description}"
              </p>
              
              <div className="flex items-center justify-center gap-4 pt-2 border-t border-white/5">
                <span className={`text-[10px] font-black px-4 py-1.5 rounded-full border-2 uppercase tracking-widest ${joker.type === 'ACTIVE' ? 'border-blue-500 text-blue-400 bg-blue-500/10' : 'border-slate-500 text-slate-400 bg-slate-500/10'}`}>
                  {joker.type} ENGINE
                </span>
                {joker.currentUses !== undefined && (
                  <div className="text-[10px] font-black text-gray-500 uppercase tracking-tighter">
                    STOCK: <span className="text-white tabular-nums">{joker.currentUses}/{joker.usesPerPuzzle}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="mt-6">
               {joker.type === 'ACTIVE' ? (
                 <button 
                  disabled={joker.currentUses !== undefined && joker.currentUses <= 0}
                  onClick={() => onActivate(joker.id)}
                  className="w-full py-5 bg-[#ff0040] hover:bg-[#ff1a53] text-white font-black rounded-2xl transition-all shadow-[0_15px_30px_rgba(255,0,64,0.3)] active:scale-95 disabled:grayscale disabled:opacity-20 text-sm uppercase tracking-[0.3em] glitch-hover"
                 >
                   {joker.currentUses === 0 ? 'ENGINE DEPLETED' : 'ENGAGE POWER'}
                 </button>
               ) : (
                 <div className="w-full py-4 bg-white/5 text-white/20 font-black rounded-xl text-[10px] text-center uppercase tracking-[0.4em] border border-white/5 italic">
                   ALWAYS ACTIVE
                 </div>
               )}
            </div>
         </div>

         {/* Close Button Footer */}
         <button onClick={onClose} className="w-full py-4 bg-black/40 text-[10px] font-black text-white/10 hover:text-white/60 uppercase tracking-[0.8em] transition-all border-t border-white/5 z-40">
           DISMISS
         </button>
      </div>
    </div>
  );
};

export default JokerInspector;
